import { Typography } from 'antd';
import './SensorChartComponent.scss';
import { ChartData } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-luxon';
import { DataTelemetry } from '../../../../gen/proto/api/gateway/v1/gateway_pb';
import { DataTelemetryData } from '../../../../models/telemetry';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { colors } from '../../../../theme/colors';

const { Title } = Typography;

interface SensorChartComponentProps {
  data: DataTelemetry[];
}

function buildSensorChartData(telemetries: DataTelemetry[]): { chartData: ChartData<'line'>; sensorTitle: string } {
  if (!telemetries || telemetries.length === 0) {
    return {
      chartData: { labels: [], datasets: [] },
      sensorTitle: 'Sensor',
    };
  }

  const sorted = [...telemetries].sort((a, b) => Number(a.timestamp?.seconds ?? 0) - Number(b.timestamp?.seconds ?? 0));

  const labels: string[] = [];
  const sensorValues: number[] = [];
  let sensorLabel = 'Sensor 0';
  let sensorUnit = '';

  for (const t of sorted) {
    const seconds = Number(t.timestamp?.seconds ?? 0);
    const date = DateTime.fromMillis(seconds * 1000).toISO();
    const telemetryData = t.data as DataTelemetryData;

    const sensor = telemetryData?.sensors?.[0];
    if (!sensor) continue;

    labels.push(date || '');
    sensorValues.push(sensor.value ?? 0);
    sensorLabel = sensor.type || sensorLabel;
    sensorUnit = sensor.unit || sensorUnit;
  }

  const displayLabel = `${sensorLabel}${sensorUnit ? ` (${sensorUnit})` : ''}`;

  return {
    chartData: {
      labels,
      datasets: [
        {
          label: sensorLabel,
          data: sensorValues,
          borderColor: colors.PRIMARY,
          backgroundColor: colors.PRIMARY,
          fill: false,
          tension: 0,
          pointRadius: 0,
        },
      ],
    },
    sensorTitle: displayLabel,
  };
}

const SensorChartComponent = ({ data }: SensorChartComponentProps) => {
  const { chartData, sensorTitle } = useMemo(() => buildSensorChartData(data || []), [data]);

  return (
    <div className="SensorChartComponent">
      <Title level={4} className="SensorChartComponent__title">
        Valores del sensor: {sensorTitle}
      </Title>
      <Chart
        type="line"
        data={chartData}
        options={{
          responsive: true,
          plugins: {
            legend: {
              position: 'top' as const,
            },
            zoom: {
              zoom: {
                wheel: { enabled: true },
                pinch: { enabled: true },
                mode: 'x',
              },
              pan: {
                enabled: true,
                mode: 'x',
              },
              limits: {
                x: { min: 'original', max: 'original' },
                y: { min: 'original', max: 'original' },
              },
            },
          },
          scales: {
            x: {
              type: 'time',
              time: { unit: 'minute' },
              title: {
                display: true,
                text: 'Tiempo',
              },
            },
            y: {
              beginAtZero: true,
              title: {
                display: true,
                text: sensorTitle,
              },
            },
          },
        }}
      />
    </div>
  );
};

export default SensorChartComponent;
